import { toast } from 'react-toastify';
import { createSubmission } from '../api/submissionService';
import { ONLINE_VOTING_TEMPLATE_IDS, GENERIC_LETTER_NOTICE_TEMPLATE_ID } from './constants';
import api from '../api/axios';
import { getLotsInRange } from './formUtils';

export const handleFormSubmit = async (options) => {
    const {
        isSubmitting, setIsSubmitting, formData, setErrors, errors,
        channelInput, isDefaultChannel, showMeetings, isOnlineVotingTemplate,
        showWindowLocks, planNumberLots, mergeFieldsConfig, replaceFields, smataConfigId,
        showReminderFields, mergedPdfUrl, mergedPdfName, isMerging, userTeamData, jwtToken,
        accessToken, navigate
    } = options;

    if (isSubmitting) {
        return;
    }

    setIsSubmitting(true);

    // Validate required fields
    const isStrata = formData.recipientSource === 'strata';
    var newErrors = {
        email: !formData.email,
        channel: !channelInput,
        inbox: isDefaultChannel && !formData.inbox_id,
        channelSuggestion: !formData.channel_id,
        meetingSuggestion: isStrata && !formData.selectedMeetingId && (isOnlineVotingTemplate || !formData.skip_meeting_update) && showMeetings && !!formData.issueType,
        planNumber: isStrata && !formData.planNumber,
        planNumberSuggestion: isStrata && errors.planNumberSuggestion,
        emailSuggestion: errors.emailSuggestion,
        sendTo: isStrata && !formData.sendTo,
        sendToValidation: isStrata && errors.sendToValidation,
        sending: isStrata && !formData.sending,
        sendingValidation: isStrata && errors.sendingValidation,
        windowLockApproval: isStrata && showWindowLocks && !formData.windowLockApproval,
        contractorName: isStrata && showWindowLocks && formData.windowLockApproval === 'yes' && !formData.contractorName,
        windowLockProceedWith: isStrata && showWindowLocks && (formData.windowLockApproval === 'yes' || formData.windowLockApproval === 'yes_qsec') && !formData.windowLockProceedWith,
        specialLeviesResolved: isStrata && (formData.smata_config_id == 6 || formData.smata_config_id == 7 || formData.smata_config_id == 65) && !formData.specialLeviesResolved,
        specialLeviesMotion: isStrata && (formData.smata_config_id == 6 || formData.smata_config_id == 7 || formData.smata_config_id == 65) && formData.specialLeviesResolved === 'yes' && !formData.specialLeviesMotion?.trim(),
        fileUpload: formData.files.length === 0,
        lotsUserTypes: isStrata && formData.sendTo === 'lotOnly' && formData.lotsUserTypes.length === 0,
        customRecipients: !isStrata && (!formData.customRecipients || formData.customRecipients.length === 0),
        lots_sprated: (() => {
            if (isStrata && formData.sendTo === 'lotOnly' && formData.lotsSelectionType === 'enter_lots') {
                if (!formData.lots_sprated) {
                    return true;
                }
                const lots = formData.lots_sprated.split(',').map(lot => lot.trim()).filter(lot => lot !== '');
                const invalidLots = lots.filter(lot => lot && !planNumberLots.some(validLot => validLot.lot_number === lot));
                if (invalidLots.length > 0) {
                    return `Invalid lot numbers: ${invalidLots.join(', ')}. Please enter valid lot numbers that exist for this plan number.`;
                }
            }
            return false;
        })(),
        lotRangeStart: isStrata && formData.sendTo === 'lotOnly' && formData.lotsSelectionType === 'range_of_lots' && !formData.lotRangeStart,
        lotRangeEnd: isStrata && formData.sendTo === 'lotOnly' && formData.lotsSelectionType === 'range_of_lots' && !formData.lotRangeEnd,
        selectedLots: isStrata && formData.sendTo === 'lotOnly' && formData.lotsSelectionType === 'show_lots' && formData.selectedLots.length === 0,
        custom_document_name: (() => {
            if (!isStrata) {
                return formData.custom_document_name
                    ? /[^a-zA-Z0-9 ]/.test(formData.custom_document_name)
                    : false;
            }
            if (/[^a-zA-Z0-9 ]/.test(formData.custom_document_name)) return true;
            if (
                Number(formData.smata_config_id) === GENERIC_LETTER_NOTICE_TEMPLATE_ID &&
                !formData.custom_document_name?.trim()
            ) {
                return 'Document name is required*';
            }
            return false;
        })(),
        mergeFields: (() => {
            if (!isStrata) return false;
            const mergeFieldErrors = {};

            mergeFieldsConfig.forEach(field => {
                if (!field.is_required) return;

                const fieldName = field.field_name;

                if (!replaceFields.includes(fieldName)) {
                    return;
                }

                const fieldValue = formData.mergeFieldsData?.[fieldName];

                if (!fieldValue || (typeof fieldValue === 'string' && fieldValue.trim() === '')) {
                    mergeFieldErrors[fieldName] = true;
                }
            });

            return Object.keys(mergeFieldErrors).length > 0 ? mergeFieldErrors : false;
        })(),
        meetingLocation: false,
        zoomMeetingId: false,
        zoomMeetingPassword: false,
        scheduled_time: formData.send_later && !formData.scheduled_time,
        customRecipientsValidation: isStrata && errors.customRecipientsValidation,
        customRecipientsValidationMessage: isStrata && errors.customRecipientsValidationMessage,
        process_street_workflow_run: isStrata && formData.preload_from_process_street && !formData.process_street_workflow_run,
        agmBuildingNotesConfirmed: isStrata && formData.smata_config_id == 6 && !formData.agmBuildingNotesConfirmed,
    };

    // Validate associated date fields
    (formData.associated_date_fields || []).forEach(field => {
        if (!formData[field]) newErrors[field] = true;
    });

    // Check if PDF is still merging or merged PDF URL exceeds 20 MB
    if (isMerging) {
        newErrors.mergedPDF = true;
    } else if (mergedPdfUrl) {
        const response = await fetch(mergedPdfUrl);
        const blob = await response.blob();
        if (blob.size > 20 * 1024 * 1024) { // 20 MB in bytes
            newErrors.fileUpload = true;
            toast.error('Merged PDF exceeds the maximum size of 20 MB.');
        }
    } else {
        if (formData.files.length != 0) {
            newErrors.fileUpload = false;
            newErrors.mergedPDF = true;
        }
    }

    setErrors(newErrors);

    // Check if there are any errors
    if (Object.values(newErrors).some(error => error)) {
        setIsSubmitting(false);
        return;
    }

    const selectedLotsArray = [];
    if (formData.sendTo === 'lotOnly') {
        if (formData.lotsSelectionType === 'enter_lots' && formData.lots_sprated && typeof formData.lots_sprated === 'string') {
            const lots = formData.lots_sprated.split(',').map(lot => lot.trim());
            selectedLotsArray.push(...lots);
        } else if (formData.lotsSelectionType === 'range_of_lots') {
            const { lots: lotsInRange } = getLotsInRange(planNumberLots, formData.lotRangeStart, formData.lotRangeEnd);
            selectedLotsArray.push(...lotsInRange.map(lot => lot.lot_number));
        } else if (formData.lotsSelectionType === 'show_lots') {
            selectedLotsArray.push(...formData.selectedLots);
        }
        formData.selectedLots = selectedLotsArray;
    } else {
        formData.lotsSelectionType = 'enter_lots';
        formData.lots_sprated = '';
        formData.lotRangeStart = '';
        formData.lotRangeEnd = '';
        formData.selectedLots = [];
    }

    // Check Reminder Date
    if (!showReminderFields) {
        formData.reminderDate = '';
        formData.sendReminders = 'no';
        formData.smsdatereminder = ''
    }

    if (formData.sendReminders == 'no') {
        formData.reminderDate = '';
    }

    let scheduledTimeWithTimezone = null;
    if (formData.send_later && formData.scheduled_time) {
        const localDate = new Date(formData.scheduled_time);
        scheduledTimeWithTimezone = localDate.toISOString();
    }

    // Append mergedPdfUrl, mergedPdfName, and colorPages to formData
    const updatedFormData = {
        ...formData,
        selectedLots: Array.isArray(formData.selectedLots) ? formData.selectedLots.join(',') : formData.selectedLots,
        lotsUserTypes: Array.isArray(formData.lotsUserTypes) ? formData.lotsUserTypes.join(',') : formData.lotsUserTypes,
        mergedPdfUrl,
        mergedPdfName,
        colorPages: formData.colorPages,
        smata_config_id: formData.smata_config_id || null,
        smata_template_doc_url: formData.smata_template_doc_url || null,
        user_team_id: userTeamData?.id,
        submitted_by: userTeamData?.email,
        issueType: formData.issueType || '',
        selectedMeetingId: formData.selectedMeetingId,
        ...(formData.send_later && { scheduled_time: scheduledTimeWithTimezone })
    };

    try {
        const token = jwtToken || localStorage.getItem('jwtToken') || accessToken || localStorage.getItem('msalToken');
        if (!token) {
            toast.error('Authentication token missing. Please login again.');
            navigate('/login');
            setIsSubmitting(false);
            return;
        }

        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const result = await createSubmission(updatedFormData);

        toast.success("Form submitted successfully!");
        navigate('/submission', { state: { submissionId: result.id } });
    } catch (error) {
        console.error("Error submitting form:", error);
        if (error.response?.status === 401) {
            toast.error('Your session has expired. Please login again.');
            navigate('/login');
        } else {
            toast.error("Failed to submit form. Please try again.");
        }
    } finally {
        setIsSubmitting(false);
    }
};
